/* Popular Builds: immediate in-progress motion; server-confirmed cart state. */
(() => {
  if (window.__mtCardQuickAddV3) return;
  window.__mtCardQuickAddV3 = true;
  let queue = Promise.resolve();
  const flights = new Set();
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const frame = () => new Promise(resolve => requestAnimationFrame(resolve));

  function refreshCart(data) {
    const html = data.sections?.['cart-icon-bubble'];
    const target = document.getElementById('cart-icon-bubble');
    if (!html || !target) return false;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (!source) return false;
    target.innerHTML = source.innerHTML;
    return true;
  }

  function pulseCart(pointer) {
    const target = document.getElementById('cart-icon-bubble');
    if (pointer && !reducedMotion() && target) {
      target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }], { duration: 180 });
    }
  }

  function startFlight(origin) {
    let cancelled = false;
    let image;
    let animation;
    const remove = () => { image?.remove(); flights.delete(cancel); };
    const cancel = () => { cancelled = true; animation?.cancel(); remove(); };
    flights.add(cancel);
    const finished = (async () => {
      if (!origin.pointer || reducedMotion() || !origin.image?.isConnected) return;
      document.querySelector('sticky-header')?.reveal?.();
      await frame();
      const target = document.getElementById('cart-icon-bubble');
      if (!target || cancelled) return;
      let end = target.getBoundingClientRect();
      if (end.top < 0) {
        await new Promise(resolve => setTimeout(resolve, 220));
        end = target.getBoundingClientRect();
      }
      const start = origin.rect;
      if (cancelled || end.bottom <= 0 || end.top >= innerHeight || start.bottom <= 0 || start.top >= innerHeight) return;
      const size = 52;
      const x = start.left + start.width / 2;
      const y = start.top + start.height / 2;
      const dx = end.left + end.width / 2 - x;
      const dy = end.top + end.height / 2 - y;
      image = document.createElement('img');
      image.className = 'mt-qa-flight';
      image.src = origin.image.currentSrc || origin.image.src;
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      image.dataset.destination = 'cart-icon-bubble';
      image.dataset.state = 'adding';
      Object.assign(image.style, { left: `${x - size / 2}px`, top: `${y - size / 2}px`, width: `${size}px`, height: `${size}px` });
      document.body.append(image);
      animation = image.animate([
        { transform: 'translate3d(0,0,0) scale(1)', opacity: .9, offset: 0 },
        { transform: `translate3d(${dx * .22}px,${dy * .6 - 50}px,0) scale(1.35)`, opacity: 1, offset: .38 },
        { transform: `translate3d(${dx * .72}px,${dy * .94 - 28}px,0) scale(.8)`, opacity: 1, offset: .76 },
        { transform: `translate3d(${dx}px,${dy}px,0) scale(.24)`, opacity: .2, offset: 1 }
      ], { duration: 600, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' });
      await animation.finished;
    })().catch(() => {}).finally(remove);
    return { cancel, finished };
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-mt-qa-trigger]');
    if (button) button.dataset.pointerActivation = event.detail > 0 ? 'true' : 'false';
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target.closest('form[data-mt-qa-form]');
    if (!form) return;
    event.preventDefault();
    const root = form.closest('[data-mt-qa]');
    const button = form.querySelector('[data-mt-qa-trigger]');
    if (root.dataset.busy === 'true' || button.disabled) return;
    const card = root.closest('.mt-qa-card');
    const origin = {
      pointer: button.dataset.pointerActivation === 'true',
      rect: button.querySelector('.mt-qa__icon').getBoundingClientRect(),
      image: card?.querySelector('.mt-hcr__image img, .card__media img')
    };
    root.dataset.busy = 'true';
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    button.classList.remove('is-added');
    const spinner = button.querySelector('.loading__spinner');
    spinner?.classList.remove('hidden');
    const error = form.querySelector('.mt-qa__error');
    const status = form.querySelector('.mt-qa__status');
    error.hidden = true;
    status.textContent = 'Adding to cart…';
    // Flight is in-progress feedback. Neither the badge nor success state is optimistic.
    const flight = startFlight(origin);
    const body = new FormData();
    body.set('id', form.elements.namedItem('id').value);
    body.set('quantity', form.elements.namedItem('quantity')?.value || '1');
    body.set('sections', 'cart-icon-bubble');
    body.set('sections_url', location.pathname);
    // Serialize cart writes; the visual feedback above does not wait in this queue.
    queue = queue.catch(() => {}).then(async () => {
      let added = false;
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'This watch could not be added. Please try again.');
        added = true;
        const refreshed = refreshCart(data);
        status.textContent = `Added to cart: ${root.dataset.variantTitle}.`;
        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 1800);
        void flight.finished.then(() => pulseCart(origin.pointer));
        if (!refreshed) {
          error.textContent = 'Added to your cart. Open the cart to review it.';
          error.hidden = false;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mt-card-quick-add', productVariantId: body.get('id'), cartData: data });
        }
      } catch (failure) {
        flight.cancel();
        status.textContent = '';
        error.textContent = added ? 'Added to your cart. Open the cart to review it.' : (failure instanceof TypeError ? 'Please check your cart before trying again; the connection was interrupted.' : failure.message);
        error.hidden = false;
      } finally {
        root.dataset.busy = 'false';
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    });
  });
  window.addEventListener('pagehide', () => { flights.forEach(cancel => cancel()); flights.clear(); });
})();
